import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  deleteAction,
  getActionsByMissionId,
} from '../../redux/actions/actionActions'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { formatTitle } from '../../utils/formatTitle'
import { Action } from '../../types/actionTypes'
import {
  DetailEntityPanel,
  DetailTable,
} from '../../components/common/detail'
import {
  canManageMission,
  DetailRowActions,
} from '../../components/common/detail/DetailRowActions'
import {
  ListPageDateCell,
  ListPagePill,
  PRIORITY_PILL,
  STATUS_PILL,
} from '../../components/common/ListPageCells'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { priorityLabelsTranslations } from '../../utils/constants'
import '../../components/common/ListPageTable.css'
import i18n from '../../i18n'
import UseAlert from '../../components/common/alert'
import { ImportTasksButton } from './import/ImportActionsWizard'

type Props = {
  IdMission: string
  onCountChange?: (count: number) => void
  /** When true, count only non-done tasks for parent KPI / badge */
  onOpenCountChange?: (count: number) => void
}

const PAGE_SIZE = 100
const DONE = new Set(['Terminé', 'Done', 'Completed', 'Fermé', 'Closed'])

const ActionListByMission: React.FC<Props> = ({
  IdMission,
  onCountChange,
  onOpenCountChange,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const roles = useSelector((state: any) => state.auth.roles)
  const loading = useSelector((state: RootState) => state.action.loading)
  const response = useSelector(
    (state: RootState) => state.action.ActionResponse
  )
  const [search, setSearch] = useState('')
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'warning',
  })

  useEffect(() => {
    if (IdMission) {
      dispatch(
        getActionsByMissionId({
          MissionId: IdMission,
          page: 1,
          pageSize: PAGE_SIZE,
        })
      )
    }
  }, [dispatch, IdMission])

  const all = response?.actions ?? []

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return all
    return all.filter((a) =>
      (a.ActionName || '').toLowerCase().includes(q)
    )
  }, [all, search])

  useEffect(() => {
    onCountChange?.(response?.total ?? all.length)
    const open = all.filter((a) => !DONE.has(a.Status || '')).length
    onOpenCountChange?.(open)
  }, [onCountChange, onOpenCountChange, response?.total, all])

  const canAdd = canManageMission(roles)

  const refresh = () => {
    if (!IdMission) return
    dispatch(
      getActionsByMissionId({
        MissionId: IdMission,
        page: 1,
        pageSize: PAGE_SIZE,
      })
    )
  }

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
      />
      <DetailEntityPanel
      listTitle={t('missionDetailsPage.sections.taskList', {
        defaultValue: 'Task list',
      })}
      searchPlaceholder={t('filterByName', { defaultValue: 'Filter by name' })}
      searchValue={search}
      onSearchChange={setSearch}
      addLabel={t('buttons.addTask', { defaultValue: 'Add New Task' })}
      onAdd={() => navigate(`/mission/${IdMission}/addaction`)}
      canAdd={canAdd}
      extraAction={
        canAdd ? (
          <ImportTasksButton
            missionId={IdMission}
            onImported={(created, failed) => {
              refresh()
              setAlert({
                open: true,
                message: failed
                  ? t('actionImport.alerts.partial', { created, failed })
                  : t('actionImport.alerts.success', { count: created }),
                severity: failed ? 'warning' : 'success',
              })
            }}
          />
        ) : null
      }
    >
      <DetailTable<Action>
        loading={loading && !response}
        loadingLabel={t('loading')}
        emptyTitle={t('missionDetailsPage.empty.title', {
          defaultValue: 'Nothing here yet',
        })}
        emptyMessage={t('missionDetailsPage.empty.tasks', {
          defaultValue:
            'No task is attached to this mission yet. Add a task to track delivery work.',
        })}
        rows={rows}
        rowKey={(r) => r._id as string}
        columns={[
          {
            key: 'name',
            header: t('task_name', { defaultValue: 'Task name' }),
            render: (r) => (
              <NavLink to={`/actiond/${r._id}`} className="incyia-name-link">
                <ListPageDescription
                  value={
                    r.ActionName ? formatTitle(r.ActionName) : undefined
                  }
                  tone="primary"
                />
              </NavLink>
            ),
          },
          {
            key: 'priority',
            header: t('Priority'),
            render: (r) => {
              const priority = Number(r.Priority)
              const style = PRIORITY_PILL[priority] ?? PRIORITY_PILL[4]
              return (
                <ListPagePill
                  label={
                    t(`priority.${priority}`, {
                      defaultValue:
                        priorityLabelsTranslations[priority] || style.label,
                    }) || style.label
                  }
                  bg={style.bg}
                  color={style.color}
                />
              )
            },
          },
          {
            key: 'status',
            header: t('Status'),
            render: (r) => {
              const style = STATUS_PILL[r.Status || ''] ?? {
                bg: '#EAF0F0',
                color: '#5F7484',
              }
              return (
                <ListPagePill
                  label={t(r.Status || '', {
                    defaultValue: r.Status || '—',
                  })}
                  bg={style.bg}
                  color={style.color}
                />
              )
            },
          },
          {
            key: 'end',
            header: t('End Date', { defaultValue: 'End date' }),
            render: (r) => (
              <ListPageDateCell value={r.DateFin} locale={i18n.language} />
            ),
          },
        ]}
        actionsHeader={t('Actions', { defaultValue: 'Action' })}
        renderActions={(r) => (
          <DetailRowActions
            roles={roles}
            onEdit={() => navigate(`/action/${r._id}`)}
            onDelete={() => dispatch(deleteAction(r._id as string))}
            deleteItem={{
              entityType: t('tasks', { defaultValue: 'Task' }),
              name: r.ActionName ? formatTitle(r.ActionName) : undefined,
              details: [
                {
                  label: t('deleteConfirm.fields.mission'),
                  value: r.MissionName
                    ? formatTitle(r.MissionName)
                    : '—',
                },
                {
                  label: t('deleteConfirm.fields.status'),
                  value: r.Status || '—',
                },
              ],
            }}
          />
        )}
      />
    </DetailEntityPanel>
    </>
  )
}

export default ActionListByMission
